import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useRouter } from 'next/router'
import { useMediaQuery } from 'shared/hooks/use-match-media'
import { useCourseData } from '../hooks/use-course-data'
import { useCourseMenu } from '../hooks/use-course-menu'
import { useGetCoursePageUrl } from '../hooks/use-course-path-url'
import { useIsUserAdminOrOwnerOfCourse } from '../hooks/use-is-user-admin-or-owner-of-course'
import { StepType } from '../hooks/use-step-router/types'
import { CourseLectureId } from '../types/course-lecture-interface'
import { CourseQuizId } from '../types/course-quiz-interface'
import { CourseStepEnum } from '../types/course-step-type'
import { getCoursePageQuery } from '../utils/get-course-page-url'
import { parseQueryId } from '../utils/parse-query-id'

export const getContentType = (route: string) => {
  const regexp = /\/\[coursePath\]\/(.*)\//gm
  const matches = regexp.exec(route)
  return matches?.[1]
}

export const useCourseStepRouter = () => {
  const router = useRouter()
  const coursePath = useGetCoursePageUrl()

  const contentRef = useRef<HTMLDivElement>(null)
  const skipFirstScrollRef = useRef(false)

  const [steps, setSteps] = useState<StepType[]>([])
  const [prevStepIndex, setPrevStepIndex] = useState<number | null>(null)
  const [stepIndex, setStepIndexState] = useState<number | null>(null)
  const [showUnavailableLecture, setShowUnavailableLecture] = useState<boolean>(true)

  const [isEnd, setIsEnd] = useState<boolean>(true)
  const [isStart, setIsStart] = useState<boolean>(true)

  const { courseData } = useCourseData()
  const courseId = courseData?.id
  const { courseMenu } = useCourseMenu({ courseId })

  const isLG = useMediaQuery('(min-width: 1024px)', false)
  const isUserAdminOrOwnerOfCourse = useIsUserAdminOrOwnerOfCourse()

  const step = stepIndex !== null ? (steps[stepIndex] as StepType | undefined) : undefined
  const isInitialized = prevStepIndex !== null

  const setIsInitialized = useCallback((value: boolean) => {
    !value && setPrevStepIndex(null)
  }, [])
  const setStepIndex = useCallback(
    (...args: Parameters<typeof setStepIndexState>) => {
      if (prevStepIndex !== stepIndex) {
        setPrevStepIndex(stepIndex)
      }
      setStepIndexState(...args)
    },
    [stepIndex],
  )

  const { lectureId: rawLectureId, quizId: rawQuizId, preview } = router.query
  const lectureId = parseQueryId<CourseLectureId>(rawLectureId)
  const quizId = parseQueryId<CourseQuizId>(rawQuizId)
  const contentType = getContentType(router.route)
  const isPreview = preview === 'true'

  useLayoutEffect(() => {
    if (step && stepIndex !== null) {
      if (steps.slice(0, stepIndex).some(step => step.available)) {
        setIsStart(false)
      } else {
        setIsStart(true)
      }

      if (steps.slice(stepIndex + 1).some(step => step.available)) {
        setIsEnd(false)
      } else {
        setIsEnd(true)
      }
    }
  }, [steps, step, stepIndex])

  useLayoutEffect(() => {
    if (courseMenu) {
      const stepsArray: StepType[] = []

      for (const menuModule of courseMenu) {
        for (const lecture of menuModule.lectures) {
          stepsArray.push({
            module: {
              name: menuModule.name,
              available: menuModule.available,
              salesPage: menuModule.salesPage,
            },
            type: CourseStepEnum.lecture,
            ...lecture,
          })
        }
        if (menuModule.quiz) {
          stepsArray.push({
            module: {
              name: menuModule.name,
              available: menuModule.available,
              salesPage: menuModule.salesPage,
            },
            type: CourseStepEnum.quiz,
            ...menuModule.quiz,
          })
        }
      }
      setSteps(stepsArray)
    }
  }, [courseMenu])

  const findStep = useCallback(() => {
    const currentStepIndex =
      contentType &&
      (lectureId || quizId) &&
      steps.findIndex(
        ({ id: stepId, type }) =>
          type === contentType && [lectureId, quizId].some(id => id === stepId),
      )

    if (stepIndex === currentStepIndex) return

    if (
      typeof currentStepIndex === 'number' &&
      !Number.isNaN(currentStepIndex) &&
      currentStepIndex !== -1 &&
      (steps[currentStepIndex].available || steps[currentStepIndex].module.salesPage)
    ) {
      setStepIndex(currentStepIndex)
    } else if (isPreview || showUnavailableLecture) {
      setStepIndex(null)
    } else {
      const availableSteps = steps.filter(({ available }) => available)
      const uncompletedAvailableSteps = availableSteps.filter(({ completed }) => !completed)

      let lastAvailableUncompletedStepIndex: number | null = null
      if (uncompletedAvailableSteps.length) {
        lastAvailableUncompletedStepIndex = steps.findIndex(
          ({ id }) => id === uncompletedAvailableSteps[0].id,
        )
      } else if (availableSteps.length) {
        lastAvailableUncompletedStepIndex = steps.findIndex(({ id }) => id === availableSteps[0].id)
      } else if (steps.length) {
        lastAvailableUncompletedStepIndex = 0
      }

      if (lastAvailableUncompletedStepIndex === stepIndex) return

      setStepIndex(
        lastAvailableUncompletedStepIndex === -1 ? null : lastAvailableUncompletedStepIndex,
      )

      coursePath &&
        lastAvailableUncompletedStepIndex &&
        steps[lastAvailableUncompletedStepIndex] &&
        router.push(
          getCoursePageQuery({
            coursePath,
            type: steps[lastAvailableUncompletedStepIndex].type,
            id: steps[lastAvailableUncompletedStepIndex].id,
          }),
          undefined,
          { shallow: true },
        )
    }
  }, [
    contentType,
    lectureId,
    quizId,
    steps,
    isPreview,
    showUnavailableLecture,
    coursePath,
    router,
    setStepIndex,
    stepIndex,
  ])

  useLayoutEffect(() => {
    if (steps.length) {
      if (!isInitialized) {
        findStep()
      }
    }
  }, [findStep, isInitialized, steps.length])

  useEffect(() => {
    if (step?.id) {
      // NOTE: I don't know why, but without the Promise the scroll doesn't work sometimes
      new Promise<void>(resolve => resolve()).then(() => {
        if (!skipFirstScrollRef.current) {
          if (isLG) {
            window.scrollTo({ top: 0, behavior: 'smooth' })
          } else if (contentRef.current?.offsetTop) {
            window.scrollTo({ top: contentRef.current.offsetTop - 170, behavior: 'smooth' })
          }
        } else {
          skipFirstScrollRef.current = false
        }
      })
    }
  }, [step?.id])

  useEffect(() => {
    if (
      coursePath &&
      router.isReady &&
      step?.id &&
      ([lectureId, quizId].every(id => id !== step.id) || contentType !== step?.type)
    ) {
      router.push(getCoursePageQuery({ coursePath, id: step.id, type: step.type }), undefined, {
        shallow: true,
      })
    }
  }, [courseId, coursePath, step?.id, step?.type, router.isReady, contentType])

  useLayoutEffect(() => {
    // NOTE: show deactivated lecture for a workspace owner or a workspace admin
    if (courseData) {
      if (!isUserAdminOrOwnerOfCourse || !contentType || (!lectureId && !quizId)) {
        setShowUnavailableLecture(false)
      }
    }
  }, [courseData, isUserAdminOrOwnerOfCourse, contentType, lectureId, quizId])

  const onOpenLecture = (id: CourseLectureId) =>
    setStepIndex(steps.findIndex(step => step.type === CourseStepEnum.lecture && step.id === id))

  const onOpenQuiz = (id: CourseQuizId) =>
    setStepIndex(steps.findIndex(step => step.type === CourseStepEnum.quiz && step.id === id))

  const onPreviousStep = () => {
    if (stepIndex !== null && !isStart) {
      setStepIndex(
        steps.findIndex(
          ({ id }) =>
            id ===
            steps
              .slice(0, stepIndex)
              .reverse()
              .find(step => step.available)?.id,
        ),
      )
    }
  }

  const onNextStep = () => {
    if (stepIndex !== null && !isEnd) {
      setStepIndex(
        steps.findIndex(
          ({ id }) => id === steps.slice(stepIndex + 1).find(step => step.available)?.id,
        ),
      )
    }
  }

  const areAllModulesUnavailableWithoutSalesPage = courseMenu?.every(
    module => !module.available && !module.salesPage,
  )

  return {
    contentRef,
    step,
    steps,
    setStepIndex,
    onOpenLecture,
    onOpenQuiz,
    onPreviousStep,
    onNextStep,
    isInitialized,
    setIsInitialized,
    showUnavailableLecture,
    setShowUnavailableLecture,
    findStep,
    isStart,
    isEnd,
    isPreview,
    areAllModulesUnavailableWithoutSalesPage,
  }
}
